// © Microsoft Corporation. All rights reserved.

import { IActionPayload, IDataConnector } from "@msnews/experiences-redux-contracts";
import { dispatchAction, getActionPayload } from "./ActionDispatchHelper";

import { ActionSenderBase } from "./ActionSenderBase";
import { Observer } from "./ActionObserver";
import { PerfTimeline } from "@msnews/diagnostics";

/**
 * Class that wraps the sending of a action to a connector (i.e. dispatch) with an explicit method, so that in the consuming code
 * it is obvious that the action is being "sent"
 * @class
 */
export class ActionSender<TActionSignature extends Function> extends ActionSenderBase<TActionSignature> {

    /**
     * Creates an instance of the `ActionSender` class.
     * @constructor
     * @param {string} type The action type.
     * @param {IDataConnector<any, any>} connector The connector instance to dispatch the action to.
     */
    constructor(
        type: string,
        observers: Observer[],
        connector: IDataConnector<any, any>) {

        super(type, observers, connector);
    }

    /**
     * Core dispatch implementation for {GlobalActionSender}
     * @param args - the arguments being dispatched
     * @returns dispatch result metadata
     */
    protected getDispatchResult(args: any[]): { payload: IActionPayload, result: any } {
        const payload = getActionPayload(this.connector, this.type, args, undefined);
        const result = dispatchAction(this.connector, payload);
        return { payload, result };
    }

    /**
     * Gets the perf timeline if applicable
     */
    protected tryGetPerfTimeline(): PerfTimeline {
        return this.connector.perfTimeline;
    }
}